import React, { useState } from 'react';
import { TeamInLeague } from '../../domain/entities';
import { AdPlaceholder } from '../shared/AdPlaceholder';
import { formatValue } from '../../utils/utilities';
import { FinancesHelpModal } from '../shared/FinancesHelpModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDollarSign } from '@fortawesome/free-solid-svg-icons';
import './FinancesScreen.css';

interface FinancesScreenProps {
    team: TeamInLeague;
    currentWeek: number;
    season: number;
    adsEnabled: boolean;
}

export const FinancesScreen = ({ team, currentWeek, season, adsEnabled }: FinancesScreenProps) => {
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const finances = team.finances;
    const lastWeekHistory = finances.history.filter(h => h.week === currentWeek - 1 && h.season === season);
    const weeklyIncome = lastWeekHistory.filter(h => h.amount > 0).reduce((sum, h) => sum + h.amount, 0);
    const weeklyExpenditure = lastWeekHistory.filter(h => h.amount < 0).reduce((sum, h) => sum + h.amount, 0);

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h1><FontAwesomeIcon icon={faDollarSign} style={{ marginRight: '8px', color: 'var(--positive-color)' }} />Finances</h1>
                <button
                    className="button"
                    onClick={() => setIsHelpOpen(true)}
                    style={{ padding: '8px 12px', fontSize: '0.9em' }}
                    title="Help"
                >
                    Help
                </button>
            </div>
            <p style={{ marginBottom: '24px', opacity: 0.9 }}>
                Track your club's income and expenses. Manage your budget wisely to fund transfers and facilities.
            </p>

            <div className="panel">
                <div className="panel-header">Overview</div>
                <div className="panel-content">
                    <div className="finances-overview-grid">
                        <div className="finance-card balance">
                            <h3>Total Balance</h3>
                            <p className={finances.balance >= 0 ? 'positive' : 'negative'}>💰 {formatValue(finances.balance)}</p>
                        </div>
                        <div className="finance-card">
                            <h3>Last Week Net</h3>
                            <p className={weeklyIncome + weeklyExpenditure >= 0 ? 'positive' : 'negative'}>📊 {formatValue(weeklyIncome + weeklyExpenditure)}</p>
                        </div>
                        <div className="finance-card income">
                            <h3>Last Week Income</h3>
                            <p className="positive">📈 {formatValue(weeklyIncome)}</p>
                        </div>
                        <div className="finance-card expenditure">
                            <h3>Last Week Expenditure</h3>
                            <p className="negative">📉 {formatValue(weeklyExpenditure)}</p>
                        </div>
                    </div>
                </div>
            </div>
            
            {adsEnabled && <AdPlaceholder />}

            <div className="panel">
                <div className="panel-header">Transaction History</div>
                <div className="table-wrapper">
                    <table className="table">
                        <thead>
                            <tr><th>S</th><th>W</th><th>Event</th><th style={{textAlign: 'right'}}>Amount</th></tr>
                        </thead>
                        <tbody>
                            {finances.history.length === 0 && (
                                <tr>
                                    <td colSpan={4} style={{textAlign: 'center'}}>No transactions yet.</td>
                                </tr>
                            )}
                            {[...finances.history].reverse().map((item, i) => (
                            <tr key={i} className={item.amount < 0 ? 'negative-bg' : 'positive-bg'}>
                                <td>{item.season}</td>
                                <td>{item.week}</td>
                                <td>{item.event}</td>
                                <td style={{textAlign: 'right'}} className={item.amount >= 0 ? 'positive' : 'negative'}>
                                    {item.amount >= 0 ? `+${formatValue(item.amount)}` : `-${formatValue(Math.abs(item.amount))}`}
                                </td>
                            </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <FinancesHelpModal isOpen={isHelpOpen} onClose={() => setIsHelpOpen(false)} />
        </>
    );
};